import type { ReactNode } from "react";
import Logo from "@/components/Logo";
import { ThemeToggle } from "@/components/ux/ThemeToggle";

interface SiteHeaderProps {
  /**
   * "large" — the full marketing top bar (logo + nav + theme toggle + login).
   * "small" — just the centered logo, for surfaces that only need branding.
   */
  size?: "large" | "small";
  
  links?: ReactNode;
  /**
   * The right-aligned login / CTA control. Differs by surface (an external
   * anchor on the landing page, an in-app sign-in button on the events index),
   * so it's passed in as a slot rather than hard-coded. Only used by "large".
   */
  login?: ReactNode;
}

/**
 * The marketing top bar — logo, nav links, theme toggle and a login slot —
 * shared by the landing page and the signed-out events index. Pass
 * `size="small"` for a branding-only header (e.g. the signed-in events index).
 */
export function SiteHeader({ size = "large", links, login }: SiteHeaderProps) {

  if (size === "small") {
    return <Logo withIcon className="md:hidden justify-center pt-4 mb-4" />;
  }

  return (
    <div className="max-w-7xl mx-auto px-6 flex items-center my-4 justify-between">
      <Logo link="/app" />
      <div className="flex items-center gap-6">
        <nav className="hidden md:flex items-center gap-6">
          {links && links}
        </nav>
        <div className="flex items-center gap-2">
          <ThemeToggle />
          {login}
        </div>
      </div>
    </div>
  );
}

export default SiteHeader;
